import React, { useRef, useState } from "react";
import { ImCross } from "react-icons/im";

interface Props {
    citation: {
        citation: string;
        title: string;
        link: string;
        book_name?: string;
        chapter?: string;
    };
    pageImages?: string[];
    bookLink?: string;
    bookError?: string | null;
    onClose: () => void;
    isLoading: boolean;
}

const BookViewerInDrawer: React.FC<Props> = ({ citation, bookLink, pageImages, bookError, onClose, isLoading }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [contentWidth, setContentWidth] = useState<number>(60); // Default: 60%

    const MIN_WIDTH = 40; // 40%
    const MAX_WIDTH = 100; // 100%
    const STEP = 10; // 10% per click
    const curatedBookName = citation.book_name;
    return (
        <div className="flex flex-col h-full">
            <div className="p-3 flex items-center justify-between relative">
                <ImCross onClick={onClose} className="cursor-pointer" />
                <p className="absolute left-1/2 transform -translate-x-1/2 text-2xl text-center px-4 text-ellipsis overflow-hidden whitespace-nowrap w-2/3" title={`${curatedBookName} - ${citation.chapter}`}>
                    {curatedBookName} - {citation.chapter}
                </p>
                <div className="flex items-center space-x-2">
                    <button
                        className="px-2 py-1 border border-gray-400 rounded bg-transparent hover:border-gray-100 disabled:hover:border-gray-400"
                        onClick={() => setContentWidth(w => Math.min(w + STEP, MAX_WIDTH))}
                        aria-label="Zoom In"
                        disabled={contentWidth + STEP > MAX_WIDTH}
                    >
                        +
                    </button>
                    <span className="text-sm text-black" style={{ display: "inline-block", width: "4ch", textAlign: "center" }}>
                        {contentWidth}%
                    </span>
                    <button
                        className="px-2 py-1 border border-gray-400 rounded bg-transparent hover:border-gray-100 disabled:hover:border-gray-400"
                        onClick={() => setContentWidth(w => Math.max(w - STEP, MIN_WIDTH))}
                        aria-label="Zoom Out"
                        disabled={contentWidth - STEP < MIN_WIDTH}
                    >
                        –
                    </button>
                </div>
            </div>

            <div
                className="flex-1 overflow-auto px-4 flex justify-center mb-4" // removed items-center
                ref={containerRef}
            >
                <div style={{ width: `${contentWidth}%`, transition: "width 0.2s" }}>
                    {isLoading ? (
                        <div className="flex h-full justify-center items-center">
                            <div className="w-12 h-12 border-4 border-green-800 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : bookError ? (
                        <div className="flex h-full justify-center items-center text-2xl">{bookError}</div>
                    ) : (
                        pageImages &&
                        pageImages.length > 0 &&
                        pageImages.map((base64String, index) => (
                            <img key={index} src={`data:image/png;base64,${base64String}`} alt={`Book page ${index + 1}`} className="w-full h-auto mb-4" />
                        ))
                    )}
                </div>
            </div>
            {bookLink && (
                <div className="p-2 bg-inherit">
                    <div className="flex flex-col items-center text-center">
                        <div className="px-2">
                            <span>View the Book: </span>
                            <a href={bookLink} target="_blank" rel="noopener noreferrer" className="cursor-pointer text-blue-600 hover:underline break-all">
                                {bookLink}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookViewerInDrawer;
